/**
 * `/fly`'s sky, ground and the one sky object hanging in it, as a single group
 * the scene adds once and re-dresses when the sky treatment changes.
 *
 * It exists so `flight-scene.ts` keeps two lines about time of day rather than
 * a dozen: everything that has to change together when ENHANCED SKY is flipped
 * — the dome's palette run, the ground plane's colour — changes here, and the
 * disc (which no treatment gates, see `time-of-day.ts`) is built once.
 *
 * There is still no `scene.fog` at any hour, and that is measured rather than
 * an omission: the original has no distance fog at all — the same palette
 * entries appear at every range in the DOSBox-X captures (`world.ts`). A dark
 * sky is not a hazy one.
 *
 * ## Why the disc is drawn where it is
 *
 * The disc rides the camera exactly as the dome does (`world.ts`'s `createSky`)
 * and for the same reason: a body at infinity has no parallax, so anchoring it
 * to the world would only give the flight a chance to fly past it. Where it
 * goes, how big it is and why its material flags are the plain opaque ones all
 * live in `celestial-disc.ts`, which the enhanced moon shares.
 *
 * ## Night is presentation only
 *
 * A night sortie is darker and that is all it is: the AI's sighting
 * (`packages/engine`'s `visible`/`stalk` paths) reads world positions and never
 * asks what colour anything is, and SUN BLIND SPOT has no sun to suppress
 * against at night (`sun-blind-spot.ts`'s `undefined` direction), so nothing in
 * the fight changes with the hour. The night run bottoms out at `BARON.PAL` 1
 * rather than at black for the player's sake, not the sim's.
 *
 * ## Two treatments, two night skies
 *
 * A night mission is a night mission under both — the hour is briefed, not
 * chosen — and the treatment decides only what hangs in it:
 *
 * - `original` — `sun.c`'s own: the banded dome, the disc `celestialBodyFor`
 *   places half a turn opposite the sun, and `star-field.ts`'s 31 `STARS.DAT`
 *   points.
 * - `enhanced` — the sky that was really overhead. `night-sky.ts` places 518
 *   catalogue stars and the moon, with its real phase, for the mission's own
 *   date, hour and region, and the authentic disc is hidden underneath it.
 *
 * `measured` is the debug reference rather than a treatment
 * (`effects-settings.ts`): it hangs nothing at all, because it is the flat
 * daytime picture that was actually captured from the game, whatever hour the
 * mission briefs.
 */

import * as THREE from "three/webgpu";
import type { CareerDate, MissionEra, MissionRegion, SkyCondition, TimeOfDay } from "@red-opus/engine";
import { celestialBodyFor, flatSkyRun, skyHorizon, skyLookFor, type PaletteColour } from "./time-of-day.ts";
import { discMesh } from "./celestial-disc.ts";
import { overcastDeckGrey, overcastDeckRamp, overcastRampIndex, overcastRepaintFor } from "./overcast-sky.ts";
import { createOvercastDeck } from "./overcast-deck.ts";
import { disposeTree } from "../three/dispose-tree.ts";
import { createGround, createSky, CELESTIAL_DISTANCE_M } from "./world.ts";
import { enhancedSkyLookFor, greyedStops } from "./enhanced-sky-look.ts";
import { createEnhancedSky } from "./enhanced-sky.ts";
import { createSkyClouds } from "./sky-clouds.ts";
import { createStarField } from "./star-field.ts";
import { createNightSky } from "./night-sky.ts";
import type { NightSkyBrief } from "./night-sky-look.ts";
import type { SkyTreatment } from "./effects-settings.ts";

export { CELESTIAL_DISTANCE_M };

/**
 * `sun.c` `0xf1ac`'s overcast repaint, wired to the camera's altitude.
 *
 * Built as a **second, flat-grey dome** that swaps visibility with the mission's
 * own rather than as a uniform, for two reasons: it is the only construction
 * that works identically for the authentic dome (a `MeshBasicMaterial`) and the
 * enhanced one (a node material whose colour is an expression), and a hidden
 * mesh costs no draw call, so the pair is still one dome on screen.
 *
 * `grey` is `overcastDeckGrey`'s — the binary's flat 84 at `measured` and at
 * noon, this hour's entry of the same 64-87 ramp otherwise. The deck used to be
 * 84 unconditionally, which made an overcast midnight the **only** sky in the
 * port still painted at noon: the user's report was "sky is just white at
 * night", and a census put a night overcast sky at rgb(186,186,186) against
 * rgb(16,25,34) for a clear one.
 *
 * `aboveDeckOnly` is everything that must not be seen *through* the deck —
 * `SUN.BMP`'s disc and, under ENHANCED, `night-sky.ts`'s catalogue stars and
 * moon. They are drawn after the dome and would otherwise hang in front of it,
 * and stars through solid cloud is the one thing an overcast sky cannot show.
 * The deck is opaque and `sun.c` agrees: below it the repaint is the whole sky.
 *
 * The frame hook rides the **ground plane**, which had none — `world.ts`'s dome
 * already uses its own `onBeforeRender` to recentre, and quietly replacing that
 * would stop the sky following the camera.
 *
 * Returns the grey dome for the caller to add; mutates everything else it was
 * given. `aboveDeckOnly` is read on every frame rather than copied, so a layer
 * that arrives later (the star field's `fetch`) can be pushed into it.
 */
const TEMP_EYE = new THREE.Vector3();

function overcastRepaint(
  dome: THREE.Mesh,
  ground: THREE.Group,
  groundColour: PaletteColour,
  grey: PaletteColour,
  aboveDeckOnly: readonly THREE.Object3D[],
): THREE.Mesh {
  const deck = createSky(flatSkyRun(overcastRampIndex(grey), grey));
  deck.visible = false;
  const plane = ground.children.find((child): child is THREE.Mesh => child instanceof THREE.Mesh);
  const planeMaterial = plane?.material;
  plane?.geometry.computeBoundingSphere();
  if (plane) {
    plane.onBeforeRender = (_renderer, _scene, camera) => {
      const repaint = overcastRepaintFor("overcast", true, camera.getWorldPosition(TEMP_EYE).y, grey);
      const underneath = repaint.sky !== undefined;
      deck.visible = underneath;
      dome.visible = !underneath;
      for (const hidden of aboveDeckOnly) hidden.visible = !underneath;
      if (planeMaterial instanceof THREE.MeshBasicMaterial) {
        planeMaterial.color.set(repaint.ground ?? groundColour);
      }
    };
  }
  return deck;
}

export interface SkyDressing {
  /**
   * Sky dome, ground plane, whatever cloud the mission's sky word puts up, and
   * (unless the mission briefs no weather) one sky object. Teardown is the
   * scene's: `flight-scene.ts` ends on `disposeTree(scene)`, which reaches
   * everything under this group.
   */
  readonly group: THREE.Group;
  /** Repaints the sky under another treatment without rebuilding the disc. */
  setSkyTreatment(state: SkyTreatment): void;
  /**
   * Sinks the **ground plane** to `metres` below the datum.
   *
   * With terrain relief drawn (`three/relief-ground.ts`) the backdrop plane is
   * no longer the ground — the relief mesh is — and a plane left at the datum
   * would slice through every valley. Dropped below the relief's own floor it
   * does the only job it still has: filling the horizon past the relief mesh's
   * rim, where nothing else is drawn.
   *
   * It moves the ground and **nothing else**. When this arrived the backdrop
   * held a dome and a plane and sinking the whole group was the same thing; it
   * now also holds the mission's clouds, its overcast dome and its stars, and
   * those sit at real altitudes that a relief floor has no business shifting.
   */
  setBackdropFloor(metres: number): void;
}

// Which picture the sky is drawn with. Owned by `effects-settings.ts` — one
// enum for the setting, the debug override and the dressing, rather than a UI
// state that has to be translated on the way in.
export type { SkyTreatment };

/**
 * The mission's weather, as the dressing needs it.
 *
 * `realisticWeather` is `RealismSettings.realisticWeather` — the Realism
 * Panel's own REALISTIC WEATHER, `RB.EXE`'s `ds:0x726b`, and the switch
 * `clouds.c` gates its entire cloud list on. It is what decides whether there
 * is cloud in the sky, and it is deliberately **not** ENHANCED SKY: the
 * original draws clouds, so the port draws them on the authentic path
 * (`sky-clouds.ts`'s header). The `enhanced` treatment gates only the paint.
 */
export interface SkyDressingContext {
  /** `mission.weather.sky`. `undefined` for a sortie that briefs none, which gets no cloud. */
  readonly sky: SkyCondition | undefined;
  /** `MissionSpec.seed`, so a sortie's cloud field is the same every time it is flown. */
  readonly seed: number;
  /**
   * `RealismSettings.realisticWeather`, which is a `Partial` on a `MissionSpec`
   * — so callers pass `!== false`, and this defaults **on**, matching both
   * shipped `.PRF` files and the Realism Panel's own default.
   */
  readonly realisticWeather?: boolean;
  /**
   * `MissionSpec.region`. Its real-world latitude and longitude are what put
   * the ENHANCED night sky's stars where they were (`night-sky-look.ts`);
   * `undefined` falls back to Verdun, the region a briefing-less flight
   * already loads.
   */
  readonly region?: MissionRegion;
  /** `MissionSpec.era` — with the seed, the date a single mission's night sky is computed for. */
  readonly era?: MissionEra;
  /** A career pilot's own calendar date, which is a real one and beats the era. */
  readonly date?: CareerDate;
}

/**
 * The original's own 31 hand-placed points out of `STARS.DAT`, which are the
 * *authentic* night sky and belong with the rest of the authentic layer —
 * `original` draws `sun.c`'s picture, and this is part of it. (They used to
 * hang under `enhanced`, which was the only treatment that drew anything at
 * night; now that it has a real sky they would only be a second, wronger
 * constellation on top of it.) `measured` still draws no stars at all: it is
 * the one flat daytime picture that was actually captured from the game,
 * whatever hour the mission briefs.
 *
 * The table arrives by `fetch`, which may land after a toggle has already torn
 * this dressing down — hence `stillCurrent`. It joins `aboveDeckOnly` at the
 * same moment it joins the scene, so an overcast deck built before the fetch
 * returned still hides it.
 */
function addAuthenticStars(
  backdrop: THREE.Group,
  horizon: PaletteColour,
  aboveDeckOnly: THREE.Object3D[],
  stillCurrent: () => boolean,
): void {
  const field = createStarField(horizon);
  void field.onLoaded.then(() => {
    if (!stillCurrent()) {
      disposeTree(field.group);
      return;
    }
    backdrop.add(field.group);
    aboveDeckOnly.push(field.group);
  });
}

/** What `night-sky.ts` needs out of a mission, gathered in one place. */
function nightBriefOf(timeOfDay: TimeOfDay | undefined, context: SkyDressingContext): NightSkyBrief {
  return {
    ...(timeOfDay ? { timeOfDay } : {}),
    ...(context.region ? { region: context.region } : {}),
    ...(context.era ? { era: context.era } : {}),
    ...(context.date ? { date: context.date } : {}),
    seed: context.seed,
  };
}

/**
 * The colour the ground is painted for this hour, treatment and sky word —
 * **the one place that decision is made**.
 *
 * `dress` paints the backdrop plane with it, and `scene-boot.ts` walks the
 * relief mesh down `GROUND_RELIEF_RAMP` by the same colour
 * (`time-of-day.ts`'s `groundRampShiftForColour`). Before this existed the two
 * were separate walks — the plane took the enhanced ground and the mesh took
 * `skyLookFor`'s — so an overcast sortie darkened the flat horizon and left the
 * hills in front of it at noon green.
 *
 * ## The sky word's floor is not a treatment, and is no longer a special case
 *
 * It used to ride the old `ENHANCED SKY` boolean, and measuring what that
 * boolean actually did to the screen is what retired it. Over a same-view A/B
 * at noon on the deterministic capture rig: under a `cloudy` briefing the
 * **ground** moved by a mean of 25.5/255 per channel and the **sky** by 10.3,
 * and under `overcast` the sky moved by *nothing at all* (below the deck both
 * states draw `sun.c`'s own flat `BARON.PAL` 84 repaint) while the ground still
 * moved. Three of the four sky words the generator draws from are non-`clear`,
 * so the toggle's usual visible signature was the field going dark — which is
 * exactly what the user reported seeing.
 *
 * So it moved onto the hour, where it belongs: the briefing is allowed to change
 * what the sortie looks like, and `enhanced` became a change to the **sky and
 * nothing else**. With the treatment collapsed to a bi-state that arrangement
 * stops being a special case at all — **every** user-facing treatment is
 * hour-driven, so the branch below is only the `measured` reference peeling off,
 * and the two a player can actually pick return the same colour by
 * construction. `enhanced-sky-look.test.ts` pins that equality per hour and per
 * sky word.
 */
export function dressingGroundColour(
  timeOfDay: TimeOfDay | undefined,
  treatment: SkyTreatment,
  sky: SkyCondition | undefined,
): PaletteColour {
  const look = skyLookFor(timeOfDay, treatment !== "measured");
  if (treatment === "measured") return look.ground;
  return enhancedSkyLookFor(timeOfDay, sky, look.ground).ground;
}

/**
 * Builds the group. `timeOfDay` is `mission.weather.timeOfDay` — `undefined`
 * for a dogfight or a duel, which briefs no weather and so gets the measured
 * daytime picture and no sky object.
 *
 * `context` is optional: a caller with no mission (the tests, `/assets`) does
 * not have to invent a sky word or a seed, and gets no cloud because a sortie
 * with no briefed sky word has none to draw.
 */
export function createSkyDressing(
  timeOfDay: TimeOfDay | undefined,
  treatment: SkyTreatment,
  context: SkyDressingContext = { sky: undefined, seed: 0 },
): SkyDressing {
  const group = new THREE.Group();
  const body = celestialBodyFor(timeOfDay);
  // Held apart from `group` so the disc, whose geometry no treatment touches,
  // is not in the subtree that re-dressing disposes. The treatment does
  // decide whether it is *shown*: `enhanced` at night hangs its own moon, in the
  // place the moon really was and with a real phase on it, and two moons in one
  // sky is one moon too many.
  const backdrop = new THREE.Group();
  group.add(backdrop);
  const disc = body ? discMesh(body) : null;
  if (disc) group.add(disc);
  // The ground plane alone rides the relief floor (`setBackdropFloor`); it is
  // held in its own child so that sinking it cannot take the clouds, the
  // overcast dome or the stars down with it. Re-made on every dress, because
  // `dress` empties `backdrop`.
  let floorGroup = new THREE.Group();
  let floorM = 0;
  const applyFloor = (): void => {
    floorGroup.position.y = floorM;
  };
  // Deliberately not the state being asked for, so the first `dress` below
  // always builds something whichever state it is handed.
  let dressed: SkyTreatment | undefined;
  // Bumped on every re-dress so the star table's `fetch`, which may land after
  // a toggle has already torn its group down, can tell that it is stale.
  let generation = 0;

  const dress = (state: SkyTreatment): void => {
    // Guarded because re-dressing disposes materials, and `dispose-tree.ts`
    // records what that costs under a long-lived `WebGPURenderer`: the
    // material's render object and its pipeline-cache entry go with it. A
    // no-op toggle should not pay that.
    if (state === dressed) return;
    dressed = state;
    const enhancedPaint = state === "enhanced";
    const weather = context.realisticWeather !== false && context.sky !== undefined;
    generation += 1;
    const built = generation;
    disposeTree(backdrop);
    backdrop.clear();
    // The hour's own ground colour comes from the authentic layer in every
    // state, so no two of them can disagree about what colour the ground is.
    const look = skyLookFor(timeOfDay, state !== "measured");
    const enhanced = enhancedSkyLookFor(timeOfDay, context.sky, look.ground);
    // The enhanced night sky is the *real* one — `night-sky.ts` places 500-odd
    // catalogue stars and the moon for the mission's own date and region. It is
    // built before the dome because the dome's halo hangs on whatever body is
    // actually up there, which at night is this moon rather than `sun.c`'s
    // half-a-turn-opposite-the-sun stand-in.
    const night = enhancedPaint && enhanced.timeOfDay === "night";
    const realSky = night
      ? createNightSky(nightBriefOf(timeOfDay, context), greyedStops(enhanced), enhanced.treatment.greyMix)
      : undefined;
    const bearing = realSky ? realSky.sky.moonDirection : body?.direction;
    if (disc) disc.visible = !night;
    // The cloud layer rides the game's own REALISTIC WEATHER switch, not the
    // treatment — see `SkyDressingContext`. So does the overcast repaint, which
    // is `sun.c`'s own behaviour and not a treatment either. What `enhanced`
    // picks is the slab's *paint*: three ramp tones instead of its baked flat 79.
    if (weather) {
      const clouds = createSkyClouds({
        sky: enhanced.sky,
        timeOfDay: enhanced.timeOfDay,
        seed: context.seed,
        sun: bearing,
        enhanced: enhancedPaint,
      });
      void clouds.onLoaded.then(() => {
        if (built === generation) backdrop.add(clouds.group);
        else disposeTree(clouds.group);
      });
    }

    const ground = createGround(undefined, dressingGroundColour(timeOfDay, state, context.sky));
    const dome = enhancedPaint ? createEnhancedSky(enhanced, bearing) : createSky(look.sky);
    floorGroup = new THREE.Group();
    floorGroup.add(ground);
    backdrop.add(dome, floorGroup);
    applyFloor();
    // Everything a solid cloud deck has to hide, gathered as it is built. The
    // disc only joins when it is actually shown (`enhanced` at night hangs its
    // own moon instead), so the repaint hook can drive plain `visible` without
    // having to remember a second reason something was hidden.
    const aboveDeckOnly: THREE.Object3D[] = [];
    if (disc?.visible) aboveDeckOnly.push(disc);
    if (realSky) {
      backdrop.add(realSky.group);
      aboveDeckOnly.push(realSky.group);
    } else if (state === "original" && timeOfDay === "night") {
      addAuthenticStars(backdrop, skyHorizon(look.sky), aboveDeckOnly, () => built === generation);
    }
    // `overcast-sky.ts`: an overcast mission is flat grey below the deck and
    // has a flat grey *ground* above it. Driven per frame off the camera's
    // altitude, which is the quantity `sun.c` reads (it reads the player's; the
    // camera is close enough, and it is what is available to a material). Built
    // last, because it is the layer that hides the others.
    if (weather && context.sky === "overcast") {
      backdrop.add(
        overcastRepaint(dome, ground, enhanced.ground, overcastDeckGrey(timeOfDay, state !== "measured"), aboveDeckOnly),
      );
      // And ENHANCED hangs a *drawn* deck in front of that repaint
      // (`overcast-deck.ts`). It is the one cloud in this port that is not the
      // original's own geometry, and it is here rather than in `sky-clouds.ts`
      // because it is not made of slabs: `COVER` places zero of those for an
      // overcast mission and this lane did not change that. Added after the
      // repaint so the two are built in the order they occlude in, and outside
      // `aboveDeckOnly` because the deck is what that list is being hidden
      // *behind*.
      if (enhancedPaint) backdrop.add(createOvercastDeck(overcastDeckRamp(timeOfDay, true)));
    }
  };

  dress(treatment);
  applyFloor();

  return {
    group,
    setSkyTreatment: (state) => {
      dress(state);
      // `dress` rebuilds the subtree, so the floor has to be re-applied after it.
      applyFloor();
    },
    setBackdropFloor: (metres) => {
      floorM = metres;
      applyFloor();
    },
  };
}
